import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, User, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts/${postId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      // Initialize with empty array if endpoint doesn't exist
      setComments([]);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setPostingComment(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      // Create comment locally first
      const comment = {
        id: Date.now().toString(),
        user_name: user.name,
        text: newComment,
        created_at: new Date().toISOString()
      };
      
      setComments([...comments, comment]);
      setNewComment('');
      toast.success('Comment posted!');
      
      // Try to post to backend (optional if endpoint doesn't exist)
      try {
        await axios.post(
          `${API}/posts/${postId}/comments`,
          { text: newComment },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.log('Backend comment API not available, using local state');
      }
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const fetchPost = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPost(response.data);
    } catch (error) {
      toast.error('Failed to fetch post');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/messages`,
        { post_id: postId, message: message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('');
      toast.success('Message sent! Check your Messages page.');
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      help: 'category-help',
      service: 'category-service',
      sale: 'category-sale',
      donation: 'category-donation',
      community: 'category-community'
    };
    return colors[cat?.toLowerCase()] || 'category-community';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-4">Post not found</h2>
          <Button onClick={() => navigate('/dashboard')}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="glassmorphism border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-orange-600" />
              <h1 className="text-2xl font-bold text-stone-900">Post Details</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card data-testid="post-detail-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-stone-900">{post.user_name}</h3>
                  <p className="text-sm text-stone-500">{new Date(post.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <span className={`category-badge ${getCategoryColor(post.category)}`}>
                {post.category}
              </span>
            </div>

            <h2 className="text-3xl font-bold text-stone-900 mb-4">{post.title}</h2>
            <p className="text-stone-700 leading-relaxed mb-6 whitespace-pre-wrap">{post.description}</p>

            {post.images && post.images.length > 0 && (
              <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                {post.images.map((img, i) => (
                  <img 
                    key={i} 
                    src={img} 
                    alt={`Post image ${i + 1}`} 
                    className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition"
                    onClick={() => window.open(img, '_blank')}
                  />
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-stone-100 text-stone-600 rounded-full text-sm">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="bg-stone-50 p-4 rounded-xl mb-6">
              <div className="flex items-center gap-2 text-stone-600">
                <MapPin className="w-5 h-5 text-orange-600" />
                <span className="font-medium">Visible within {post.radius / 1000} km radius</span>
              </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto rounded-full btn-primary">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Connect
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send a Message</DialogTitle>
                  <DialogDescription>
                    Start a conversation with {post.user_name}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message</Label>
                    <Input
                      id="message"
                      placeholder="Hi, I'm interested in your post..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim()}
                    className="w-full rounded-full btn-primary"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Comments Section */}
            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Comments ({comments.length})
              </h3>

              {/* Comment Input */}
              <div className="mb-6 flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !postingComment) {
                      handlePostComment();
                    }
                  }}
                  className="flex-1 rounded-full"
                />
                <Button
                  onClick={handlePostComment}
                  disabled={postingComment || !newComment.trim()}
                  className="rounded-full bg-orange-600 hover:bg-orange-700"
                >
                  {postingComment ? 'Posting...' : 'Post'}
                </Button>
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">{comment.user_name}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(comment.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-gray-700">{comment.text}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}